const dateTimeFormatter = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const dateFormatter = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
})

const numberFormatter = new Intl.NumberFormat('cs-CZ')

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date)
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date)
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value)
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
]

const relativeFormatter = new Intl.RelativeTimeFormat('cs-CZ', { numeric: 'auto' })

export function formatRelative(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  const diffSeconds = (date.getTime() - now.getTime()) / 1000

  for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
    if (Math.abs(diffSeconds) >= secondsInUnit) {
      return relativeFormatter.format(Math.round(diffSeconds / secondsInUnit), unit)
    }
  }

  return 'před chvílí'
}

const ROLE_LABELS: Record<string, string> = {
  ROLE_USER: 'Uživatel',
  ROLE_ADMIN: 'Administrátor',
  ROLE_SUPER_ADMIN: 'Super administrátor',
}

export function formatRole(role: string): string {
  return ROLE_LABELS[role] ?? role
}

/**
 * Nejvyšší role uživatele — v seznamu nemá smysl vypisovat všechny,
 * ROLE_USER má stejně každý.
 */
export function primaryRole(roles: string[]): string {
  if (roles.includes('ROLE_SUPER_ADMIN')) return 'ROLE_SUPER_ADMIN'
  if (roles.includes('ROLE_ADMIN')) return 'ROLE_ADMIN'
  return 'ROLE_USER'
}

// ─── Ceny a platnost nabídek ───────────────────────────────────────────────

const priceFormatter = new Intl.NumberFormat('cs-CZ', {
  style: 'currency',
  currency: 'CZK',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Cena v Kč, např. „169,90 Kč“. */
export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return priceFormatter.format(value)
}

/** Cena za měrnou jednotku, např. „21,24 Kč/kg“. */
export function formatUnitPrice(value: number | null | undefined, unit: string | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return unit ? `${formatPrice(value)}/${unit}` : formatPrice(value)
}

const shortDateFormatter = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric' })

/**
 * Datum YYYY-MM-DD bez časové zóny — `new Date('2026-10-19')` je půlnoc UTC
 * a v západních zónách by se posunula na předchozí den.
 */
function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/** Platnost nabídky nebo letáku: „19. 10. – 24. 10. 2026“, „od 19. 10. 2026“. */
export function formatValidity(from: string | null | undefined, to: string | null | undefined): string {
  const start = from ? parseIsoDate(from) : null
  const end = to ? parseIsoDate(to) : null

  if (start && end) {
    const sameYear = start.getFullYear() === end.getFullYear()
    const left = sameYear ? shortDateFormatter.format(start) : dateFormatter.format(start)
    return `${left} – ${dateFormatter.format(end)}`
  }
  if (start) return `od ${dateFormatter.format(start)}`
  if (end) return `do ${dateFormatter.format(end)}`
  return '—'
}

/** Dnešní datum ve tvaru YYYY-MM-DD podle místního času (filtr activeOn). */
export function todayIso(now: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Datum posunuté o počet dní zpět, ve tvaru YYYY-MM-DD (filtr since). */
export function daysAgoIso(days: number, now: Date = new Date()): string {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days)
  return todayIso(date)
}

/** České skloňování podle počtu: 1 stránka, 2–4 stránky, 5+ stránek. */
export function plural(count: number, one: string, few: string, many: string): string {
  const abs = Math.abs(count)
  if (abs === 1) return one
  if (abs >= 2 && abs <= 4) return few
  return many
}

export interface ValidityStatus {
  label: string
  tone: 'active' | 'upcoming' | 'ended'
}

const DAY_MS = 24 * 60 * 60 * 1000

function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / DAY_MS)
}

function days(count: number): string {
  return `${count} ${plural(count, 'den', 'dny', 'dní')}`
}

/**
 * Platnost vztažená k dnešku: „platí ještě 3 dny“ (včetně dneška),
 * „začíná za 2 dny (platí 7 dní)“, „skončila“. Bez data začátku i konce null.
 */
export function formatValidityStatus(
  from: string | null | undefined,
  to: string | null | undefined,
  now: Date = new Date(),
): ValidityStatus | null {
  const start = from ? parseIsoDate(from) : null
  const end = to ? parseIsoDate(to) : null
  if (!start && !end) return null
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  if (end && daysBetween(today, end) < 0) return { label: 'skončila', tone: 'ended' }

  if (start && daysBetween(today, start) > 0) {
    const until = daysBetween(today, start)
    const label = until === 1 ? 'začíná zítra' : `začíná za ${days(until)}`
    return { label: end ? `${label} (platí ${days(daysBetween(start, end) + 1)})` : label, tone: 'upcoming' }
  }

  if (!end) return { label: 'platí', tone: 'active' }
  const left = daysBetween(today, end) + 1
  return { label: left === 1 ? 'platí dnes naposledy' : `platí ještě ${days(left)}`, tone: 'active' }
}
